/**
 * Escape do conteúdo dinâmico dos alertas do `vue2-simplert-plugin`
 * (TMS-6449 — SEC-01, CWE-79).
 *
 * **Por que este módulo existe.** O Simplert renderiza o `title` por
 * interpolação (`_v(_s(title))`, escapado pelo Vue) mas a `message` por
 * `domProps:{innerHTML:_s(message)}` — ou seja, `v-html` compilado. Toda string
 * que chega em `message` é HTML executável. A tela Gerar Doc põe ali razão
 * social de pessoa jurídica, que é validada só por comprimento
 * (`string|max:60`, sem restrição de charset) e cuja barreira de escrita é
 * baixa: `POST /dashboard/store` grava o campo sem `permission:` e o consumer
 * Kafka de transportadoras escreve sem usuário nenhum. `max:60` não protege —
 * `<img src=x onerror=eval(name)>` tem 30 caracteres.
 *
 * **Por que o escape é aqui e não no backend.** A MESMA string montada pelo
 * `PartnerRedispatchAlertService` é persistida em `doc_fiscal_log.descr_completo`
 * e exibida na aba "Erros" da nota com `{{ }}`, que já escapa. Escapar na origem
 * gravaria `&lt;` no banco e mostraria a entidade literal ao operador,
 * corrompendo a auditoria — o registro precisa guardar o texto exatamente como
 * foi exibido no modal. O lugar certo de escapar é o sink.
 *
 * **Regras de autoria** (impostas pelo harness `tools/verificar-escape-alerta.mjs`,
 * que espelha este arquivo num diretório temporário `{"type":"module"}`):
 *   1. nenhum import — nem de terceiros, nem de outro módulo do `src`;
 *   2. nada de `?.` nem de `??`: o `isend-frontend` não tem Babel e esses
 *      operadores quebram em runtime nos navegadores alvo.
 */

/**
 * Os cinco caracteres que mudam o significado de um fragmento de HTML. `&` vem
 * primeiro no mapa por clareza; a troca é feita em uma única passada de regex,
 * então não há risco de duplo escape de `&`.
 */
const ENTIDADES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Converte texto em HTML inerte.
 *
 * `\n`, `\r` e `\t` passam intactos de propósito: quem preserva a quebra de
 * linha é o `white-space: pre-line` do wrapper, e trocar `\n` por `<br>` seria
 * reabrir o sink que esta função fecha.
 *
 * @param {*} valor texto a escapar; `null`/`undefined` viram string vazia
 * @returns {String} o mesmo texto, sem nenhum caractere de marcação ativo
 */
export function escaparHtml(valor) {
  if (valor === null || valor === undefined) {
    return "";
  }

  return String(valor).replace(/[&<>"']/g, function (caractere) {
    return ENTIDADES[caractere];
  });
}

/**
 * Monta a `message` do Simplert para a mensagem pronta do backend.
 *
 * O wrapper com `white-space: pre-line` é o único motivo de existir HTML aqui:
 * a copy de múltiplos parceiros (RN-08) separa as parceiras por `\n` e, dentro
 * de `innerHTML`, `\n` não quebra linha sozinho. Com o texto escapado, os `\n`
 * continuam sendo `\n` de verdade no nó de texto e o `pre-line` continua
 * quebrando — o que muda é que `<` e `>` da razão social deixam de virar tag.
 *
 * @param {String} mensagem texto do alerta, já montado no backend
 * @returns {String} HTML seguro para `message` do `$Simplert.open`
 */
export function mensagemAlertaHtml(mensagem) {
  return (
    '<div style="white-space: pre-line">' + escaparHtml(mensagem) + "</div>"
  );
}
